// Desktop keyboard shortcuts for moving around chat, like Discord:
//   Esc              mark the open channel read (only when nothing else wants Esc)
//   ⌘K / Ctrl+K      quick switcher
//   Alt+↑ / Alt+↓    previous / next channel in sidebar order
//   Alt+Shift+↑ / ↓  previous / next unread channel
import { useEffect, useRef } from "react";

export interface ShortcutHandlers {
  /** Esc with nothing else to close or cancel. */
  markRead: () => void;
  openSwitcher: () => void;
  /** dir: -1 up, +1 down. unread: skip channels with nothing unread. */
  step: (dir: -1 | 1, unread: boolean) => void;
  /** Page state that already owns Esc (a reply being written, a rename, a thread reply…). */
  escTaken: () => boolean;
}

/**
 * Things on screen that close or cancel on Esc themselves. If any is open when Esc is pressed, Esc is theirs.
 * Checked in the capture phase, before any of them has reacted (and possibly vanished).
 */
const ESC_OWNERS = ".overlay, .media-picker, .reply-bar, .emoji-picker, .gif-picker, .sheet-backdrop, .autocomplete, .inline-edit, .quick-switcher, .list-picker, .msg-more, .row-menu";

export function useChatShortcuts(handlers: ShortcutHandlers) {
  const h = useRef(handlers);
  h.current = handlers;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        h.current.openSwitcher();
        return;
      }
      if (e.altKey && !mod && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        // A picker or menu might use arrows; leave them alone.
        if (document.querySelector(ESC_OWNERS)) return;
        e.preventDefault();
        h.current.step(e.key === "ArrowUp" ? -1 : 1, e.shiftKey);
        return;
      }
      if (e.key === "Escape" && !mod && !e.altKey && !e.shiftKey) {
        const target = e.target as HTMLElement | null;
        // Esc in a field that isn't the message box (renaming, adding a channel) cancels that field.
        const otherField = target?.matches?.("input, select, textarea:not([id^='composer-'])");
        if (otherField || document.querySelector(ESC_OWNERS) || h.current.escTaken()) return;
        h.current.markRead();
      }
    };
    // Capture: we see the state before the composer, pickers or editor react to the same key.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
}
